import api from "@/services/api";

// ─── Model ───
export class VendorEvaluationModel {
  constructor(data = {}) {
    this.id = data.id || null;
    this.document_id = data.document_id || null;
    this.mst_corporate_id = data.mst_corporate_id;
    this.eval_type = data.eval_type || "SUPPLIER" || null;
    this.nama_supplier = data.nama_supplier || ""; // display only, dari query param
    this.jenis_supplier = data.jenis_supplier || null; // INT, id dari m004_parameter
    this.jenis_supplier_lainnya = data.jenis_supplier_lainnya || "";
    this.jenis_produk_jasa = data.jenis_produk_jasa || null; // INT, id dari m004_parameter
    this.penilai_id = data.penilai_id || null; // dari user login
    this.penilai_name = data.penilai_name || ""; // display only
    this.evaluation_date = data.evaluation_date || "";

    // { [parameter_id]: score }
    this.nilai = data.nilai || {};

    this.rata_rata_nilai = data.rata_rata_nilai ?? null;
    this.kesimpulan = data.kesimpulan || null;
    this.notes = data.notes || null;
    this.status_approval = data.status_approval || "DRAFT";
    this.evaluation_id = data.evaluation_id || null;
    this.approval_status = data.approval_status || null;
    this.approver_employee_id = data.approver_employee_id || null;
    this.approver_action_by_name = data.approver_action_by_name || null;
    this.approver_action_on = data.approver_action_on || null;
    this.approver_notes = data.approver_notes || null;
    this.details = data.details || [];
  }

  toPayload() {
    const evalType = (this.eval_type || "SUPPLIER").toUpperCase();
    // DB: jenis_supplier NOT NULL — TRANSPORT tidak wajib di UI
    const jenisSupplier =
      this.jenis_supplier !== null &&
      this.jenis_supplier !== undefined &&
      this.jenis_supplier !== ""
        ? this.jenis_supplier
        : evalType === "TRANSPORT"
        ? 0
        : this.jenis_supplier;

    const jenisProdukJasa =
      this.jenis_produk_jasa !== null &&
      this.jenis_produk_jasa !== undefined &&
      this.jenis_produk_jasa !== ""
        ? this.jenis_produk_jasa
        : evalType === "TRANSPORT"
        ? 0
        : this.jenis_produk_jasa;

    return {
      document_id: this.document_id,
      mst_corporate_id: this.mst_corporate_id,
      eval_type: evalType,
      jenis_supplier: jenisSupplier,
      jenis_supplier_lainnya: this.jenis_supplier_lainnya || null,
      jenis_produk_jasa: jenisProdukJasa,
      penilai_id: this.penilai_id,
      penilai_name: this.penilai_name,
      evaluation_date: this.evaluation_date,
      total_score: this.rata_rata_nilai,
      kesimpulan: this.kesimpulan,
      notes: this.notes || null,
      details: Object.entries(this.nilai).map(([parameter_id, score]) => ({
        parameter_id: Number(parameter_id),
        score,
      })),
      // penilai_id, penilai_name, created_by ditambahkan backend dari req.user
    };
  }
}

// ─── HTTP ───

async function httpFetchMasterData(evalType = "SUPPLIER", documentId = null) {
  const params = { eval_type: evalType || "SUPPLIER" };
  if (documentId) params.document_id = documentId;
  const res = await api.get("/vendor-evaluation", { params });
  return res.data.data; // { categories, options, eval_type }
}
async function httpCheckCreateAuthorization() {
  const res = await api.get(`/vendor-evaluation/can-create`);
  return res.data;
}
async function httpGetById(id) {
  const res = await api.get(`/vendor-evaluation/${id}`);
  return res.data.data;
}
async function httpCreateEvaluation(payload) {
  const res = await api.post("/vendor-evaluation", payload);
  return res.data;
}
async function httpSubmitEvaluation(evaluationId) {
  const res = await api.put(`/vendor-evaluation/${evaluationId}/submit`);
  return res.data;
}
async function httpApproveEvaluation(evaluationId, notes) {
  const res = await api.put(`/vendor-evaluation/${evaluationId}/approve`, {
    notes,
  });
  return res.data;
}
async function httpRejectEvaluation(evaluationId, reason) {
  const res = await api.put(`/vendor-evaluation/${evaluationId}/reject`, {
    reason,
  });
  return res.data;
}
async function httpGetPendingApproval() {
  const res = await api.get(`/vendor-evaluation/pending-approval`);
  return res.data;
}

// ─── Public API (ex-controller) ───

export const THRESHOLD = 2;
export const NILAI_SCALE = [3, 2, 1];

/** Resolve eval_type from partner type name / id / explicit query */
export function resolveEvalType(routeQuery = {}) {
  const explicit = (
    routeQuery.eval_type ||
    routeQuery.evalType ||
    ""
  ).toUpperCase();
  if (explicit === "TRANSPORT" || explicit === "SUPPLIER") return explicit;

  const name = (
    routeQuery.partnerTypeName ||
    routeQuery.partner_type_name ||
    routeQuery.partnerType ||
    ""
  ).toLowerCase();

  // Transportasi Vendor / Transportasi Customer
  if (name.includes("transport")) return "TRANSPORT";

  return "SUPPLIER";
}

export async function initForm(routeQuery) {
  const { documentId, vendorId, namaSupplier } = routeQuery;

  if (!vendorId || !namaSupplier) {
    throw new Error("Akses tidak valid, buka dari halaman Monitoring Document");
  }

  let evalType = resolveEvalType(routeQuery);

  const master = await httpFetchMasterData(evalType, documentId || null);
  const { categories, options } = master;
  // Backend bisa override berdasarkan partner type dokumen
  if (master.eval_type === "TRANSPORT" || master.eval_type === "SUPPLIER") {
    evalType = master.eval_type;
  }

  const nilai = {};
  categories.forEach((cat) =>
    cat.parameters.forEach((p) => {
      nilai[p.id] = null;
    }),
  );

  const model = new VendorEvaluationModel({
    document_id: documentId ? Number(documentId) : null,
    mst_corporate_id: Number(vendorId),
    nama_supplier: namaSupplier,
    eval_type: evalType,
    nilai,
  });

  return { categories, options, model, evalType };
}

export function evaluateForm(model, kesimpulanOptions = []) {
  const values = Object.values(model.nilai).filter(
    (v) => v !== null && v !== undefined,
  );

  const totalParams = Object.keys(model.nilai).length;

  const isComplete = values.length === totalParams && totalParams > 0;

  const rataRata = values.length
    ? values.reduce((a, b) => a + b, 0) / values.length
    : null;

  let kesimpulanId = null;
  let kesimpulanLabel = null;

  if (isComplete && rataRata !== null) {
    const label = rataRata >= THRESHOLD ? "DILANJUTKAN" : "TIDAK DILANJUTKAN";

    const item = kesimpulanOptions.find((x) => x.description === label);

    kesimpulanId = item?.value ?? null;
    kesimpulanLabel = label;
  }

  return {
    isComplete,
    rataRata,
    kesimpulanId,
    kesimpulanLabel,
  };
}
export async function getById(id) {
  return httpGetById(id);
}
export async function submitForEvaluationApproval(evaluationId) {
  return httpSubmitEvaluation(evaluationId);
}
export async function approveEvaluation(evaluationId, notes) {
  return httpApproveEvaluation(evaluationId, notes);
}
export async function checkCreateAuthorization() {
  return httpCheckCreateAuthorization();
}
export async function rejectEvaluation(evaluationId, reason) {
  return httpRejectEvaluation(evaluationId, reason);
}
export async function getPendingApproval() {
  return httpGetPendingApproval();
}

export async function submitForm(model) {
  const payload = model.toPayload();
  return httpCreateEvaluation(payload);
}

async function httpGetEvaluationList(status = "ALL") {
  const res = await api.get(`/vendor-evaluation/list`, {
    params: { status },
  });
  return res.data;
}

export async function getEvaluationList(status = "ALL") {
  return httpGetEvaluationList(status);
}

export async function initFormFromRejected(routeQuery) {
  const { documentId, vendorId, namaSupplier, from_rejected_id } = routeQuery;

  if (!vendorId || !namaSupplier) {
    throw new Error("Akses tidak valid: vendor tidak ditemukan");
  }

  let evalType = resolveEvalType(routeQuery);
  const master = await httpFetchMasterData(evalType, documentId || null);
  const { categories, options } = master;
  if (master.eval_type === "TRANSPORT" || master.eval_type === "SUPPLIER") {
    evalType = master.eval_type;
  }

  const nilai = {};
  categories.forEach((cat) =>
    cat.parameters.forEach((p) => {
      nilai[p.id] = null;
    }),
  );

  let rejectedNotes = null;
  let rejectedBy = null;
  if (from_rejected_id) {
    try {
      const old = await httpGetById(from_rejected_id);
      if (old?.model?.nilai) {
        Object.keys(nilai).forEach((k) => {
          if (old.model.nilai[k] != null) nilai[k] = old.model.nilai[k];
        });
      }
      rejectedNotes = old?.model?.approver_notes || null;
      rejectedBy = old?.model?.approver_action_by_name || null;
    } catch (e) {
      console.warn("Prefill dari evaluasi ditolak gagal:", e.message);
    }
  }

  const model = new VendorEvaluationModel({
    document_id: documentId ? Number(documentId) : null,
    mst_corporate_id: Number(vendorId),
    nama_supplier: namaSupplier,
    eval_type: evalType,
    nilai,
  });

  return {
    categories,
    options,
    model,
    evalType,
    isRedo: !!from_rejected_id,
    rejectedNotes,
    rejectedBy,
  };
}
